import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getURI } from '../../api/apiConfig';
import { UserContext } from '../../context/UserContext';

// nodejs library to set properties for components
import cx from 'classnames';
import PropTypes from 'prop-types';

// @mui/material components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { makeStyles } from '@mui/styles';

// material-ui icons
import Menu from '@mui/icons-material/Menu';
import MoreVert from '@mui/icons-material/MoreVert';
import ViewList from '@mui/icons-material/ViewList';

// core components
import Button from 'components/CustomButtons/Button';
import AdminNavbarLinks from './AdminNavbarLinks';

import styles from 'assets/jss/material-dashboard-pro-react/components/adminNavbarStyle';

// application components
import { PromptAlert } from 'features/ux/RouterPrompt';
import ProfileEditor from './ProfileEditor';
import ReadNotification from './ReadNotification';
import SelectClient from './SelectClient';
import SelectEnvironment from './SelectEnvironment';

import { Box } from '@mui/material';
import { useLazyGetSessionQuery } from 'api/session.api';
import { useLazyGetTenantStatsQuery } from 'api/tenant.api';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
import dashboardDates from 'views/Dashboard/dashboardDates.const';
import {
  setImpersonationMode,
  setToken
} from '../../features/session/sessionSlice';

const useStyles = makeStyles(styles);
let shouldExitDeferred = false;

export default function AdminNavbar(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [userContext, setUserContext] = useContext(UserContext);
  const viewIsDirty = useSelector(selectViewIsDirty);
  const [showDirtySwitchAlert, setShowDirtySwitchAlert] = React.useState(false);
  const [triggerGetStats] = useLazyGetTenantStatsQuery();
  const [triggerGetSession] = useLazyGetSessionQuery();
  

  const { color, rtlActive, brandText } = props;
  const appBarClasses = cx({
    [' ' + classes[color]]: color,
  });
  const sidebarMinimize =
    classes.sidebarMinimize +
    ' ' +
    cx({
      [classes.sidebarMinimizeRTL]: rtlActive,
    });

  const [editProfile, setEditProfile] = React.useState(false);

  const handleEditProfile = () => {
    setEditProfile(true);
  };

  const handleCloseProfile = () => {
    setEditProfile(false);
  };

  const [readNotification, setReadNotification] = React.useState(false);
  const [notification, setNotification] = React.useState(null);
  const handleReadNotification = (notification) => {
    setNotification(notification);
    setReadNotification(true);
  };

  const handleCloseNotification = () => {
    setReadNotification(false);
  };

  const [clientSelectOpen, setClientSelectOpen] = React.useState(false);

  const handleSwitchOrganizations = () => {
    setClientSelectOpen(true);
  };

  const [environmentSelectOpen, setEnvironmentSelectOpen] =
    React.useState(false);

  const handleSwitchEnvironment = () => {
    setEnvironmentSelectOpen(true);
  };

  const handleExitOrganization = async () => {
    if (viewIsDirty) {
      setShowDirtySwitchAlert(true);
      shouldExitDeferred = true;
      return;
    }

    const exitImpersonationSession = async (token) =>{
      const sessionData = await triggerGetSession({ token }).unwrap()
      dispatch(setToken(sessionData?.token))
    }

    fetch(getURI('resume'), {
      // change this to fetchwithtimeout
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }).then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        setUserContext((oldValues) => {
          return { ...oldValues, token: data.token };
        });
        await exitImpersonationSession(data.token);
        dispatch(setImpersonationMode(false));
      } else {
        setUserContext((oldValues) => {
          return { ...oldValues, token: null };
        });
      }
    });
  };

  const switchClient = () => {
    setClientSelectOpen(false);
  };

  const switchEnvironment = () => {
    setEnvironmentSelectOpen(false);
    const period = dashboardDates[0].replace(/\s+/g, '').toLowerCase();
    triggerGetStats({period}).unwrap();
  };

  useEffect(() => {
    if (shouldExitDeferred) {
      shouldExitDeferred = false;
      handleExitOrganization();
    }
  }, [viewIsDirty]);

  return (
    (<AppBar className={classes.appBar + appBarClasses}>
      {showDirtySwitchAlert && (
        <PromptAlert
          onConfirm={() => {
            setShowDirtySwitchAlert(false);
            dispatch(setViewIsDirty(false));
          }}
          onCancel={() => {
            setShowDirtySwitchAlert(false);
            shouldExitDeferred = false;
          }}
        />
      )}
      <Toolbar className={classes.container}>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <div className={sidebarMinimize}>
            {props.miniActive ? (
              <Button
                justIcon
                round
                color='white'
                onClick={props.sidebarMinimize}
              >
                <ViewList className={classes.sidebarMiniIcon} />
              </Button>
            ) : (
              <Button
                justIcon
                round
                color='white'
                onClick={props.sidebarMinimize}
              >
                <MoreVert className={classes.sidebarMiniIcon} />
              </Button>
            )}
          </div>
        </Box>
        <div className={classes.flex}>
          {/* Here we create navbar brand, based on route name */}
          <Button className={classes.title} color='transparent'>
            {brandText}
          </Button>
        </div>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <AdminNavbarLinks
            rtlActive={rtlActive}
            editProfileHandler={handleEditProfile}
            switchOrganizationsHandler={handleSwitchOrganizations}
            exitOrganizationHandler={handleExitOrganization}
            switchEnvironmentHandler={handleSwitchEnvironment}
            readNotificationHandler={handleReadNotification}
          />
        </Box>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Button
            className={classes.appResponsive}
            color='transparent'
            justIcon
            aria-label='open drawer'
            onClick={props.handleDrawerToggle}
          >
            <Menu />
          </Button>
        </Box>
      </Toolbar>
      <ProfileEditor open={editProfile} handleClose={handleCloseProfile} />
      <SelectClient
        clientSelectOpen={clientSelectOpen}
        setClientSelectOpen={setClientSelectOpen}
        switchClient={switchClient}
      />
      <SelectEnvironment
        environmentSelectOpen={environmentSelectOpen}
        setEnvironmentSelectOpen={setEnvironmentSelectOpen}
        switchEnvironment={switchEnvironment}
      />
      <ReadNotification
        open={readNotification}
        notification={notification}
        handleClose={setReadNotification}
      />
    </AppBar>)
  );
}

AdminNavbar.propTypes = {
  color: PropTypes.oneOf(['primary', 'info', 'success', 'warning', 'danger']),
  rtlActive: PropTypes.bool,
  brandText: PropTypes.string,
  miniActive: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
  sidebarMinimize: PropTypes.func,
};
